import { nextTick } from 'vue';

/**
 * useFormattedInput — cursor-position-preserving format-on-update composable.
 *
 * ## Problem
 * When a formatter inserts punctuation characters (dots, slashes, dashes) into a
 * text input, the input's value length changes.  Vue's two-way binding re-sets the
 * native value after the DOM event, which moves the browser cursor to the end of the
 * string.  This composable computes a corrected cursor position and restores it after
 * the next DOM paint.
 *
 * ## Design choices
 * - **Formatters are responsible for their own stripping.**  `useFormattedInput` does
 *   NOT strip non-digit characters from `rawValue` before passing it to the formatter.
 *   The raw string emitted by `@update:model-value` already reflects what the user
 *   typed; the formatter decides what to keep (e.g. `replace(/\D/g, '')`).  This keeps
 *   the composable generic and compatible with formatters that legitimately allow
 *   non-digit input (e.g. mixed alphanumeric masks).
 * - **Caret offset = formatted.length − rawValue.length.**  Because formatters
 *   typically insert punctuation (net length gain), this offset is usually positive.
 *   For deletion paths the offset may be zero or negative, which is also handled.
 * - **nextTick guard.**  The caret restore runs in `nextTick` so that Vue has already
 *   flushed the reactive assignment and the DOM reflects the new value before we call
 *   `setSelectionRange`.
 * - **Active-element guard.**  If the currently focused element is not an
 *   `HTMLInputElement` or `HTMLTextAreaElement`, the caret restore is skipped safely.
 *
 * ## Caveats
 * - The composable relies on `document.activeElement` at call time.  If focus has
 *   moved away between the `@update:model-value` event and `nextTick`, the restore is
 *   skipped (the guard prevents an invalid `setSelectionRange` call on an unrelated
 *   element).
 * - IME (input method editor) composition events are not explicitly handled.  In
 *   practice this is fine for CNPJ/CEP/phone masks because those fields contain only
 *   ASCII digits.
 *
 * ## Usage
 * ```ts
 * const { onUpdate } = useFormattedInput(formatCpfCnpj);
 *
 * // In template:
 * // :model-value="formData.document"
 * // @update:model-value="v => { formData.document = onUpdate(v ?? '') }"
 * ```
 *
 * @param formatter - Pure function that takes a raw string and returns a formatted
 *   string.  Must be synchronous.
 */
export function useFormattedInput(formatter: (raw: string) => string) {
  /**
   * Formats `rawValue` via the supplied `formatter`, schedules a caret restore on the
   * currently focused input, and returns the formatted string so the caller can assign
   * it to the reactive model.
   *
   * @param rawValue - The value emitted by `@update:model-value` (what the user just typed).
   * @returns The formatted string.
   */
  function onUpdate(rawValue: string): string {
    const activeEl = document.activeElement;
    const isInputEl =
      activeEl instanceof HTMLInputElement ||
      activeEl instanceof HTMLTextAreaElement;

    // Capture cursor position before formatting.
    const oldCursor = isInputEl ? (activeEl.selectionStart ?? rawValue.length) : rawValue.length;

    const formatted = formatter(rawValue);

    if (isInputEl) {
      const offset = formatted.length - rawValue.length;
      const restorePos = Math.max(0, oldCursor + offset);

      nextTick(() => {
        // Re-check: focus may have moved during the tick.
        if (document.activeElement === activeEl) {
          activeEl.setSelectionRange(restorePos, restorePos);
        }
      });
    }

    return formatted;
  }

  return { onUpdate };
}
